import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'
import { Button } from './Button'
import { Spinner } from './Spinner'

/**
 * Loading, empty and error states.
 *
 * The brief is specific about these and it is right to be: they are most of
 * what a working day looks like. An empty state invites an action. An error
 * says what happened and what to do about it. Neither shrugs.
 */

export function Loading({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div className={cn('flex items-center justify-center gap-2.5 py-16 text-taupe', className)} role="status">
      <Spinner />
      <span className="label">{label}</span>
    </div>
  )
}

export interface EmptyStateProps {
  /** What is not here yet, as a noun phrase: 'No enquiries yet'. */
  title: string
  /** One sentence on what will fill it, or why it is empty right now. */
  body?: string
  action?: { label: string; onClick: () => void } | undefined
  className?: string
  children?: ReactNode
}

export function EmptyState({ title, body, action, className, children }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center gap-3 rounded-xl bg-paper px-6 py-14 text-center', className)}>
      <h3 className="text-[length:var(--text-base)] font-semibold">{title}</h3>
      {body ? <p className="measure text-[length:var(--text-sm)] text-taupe">{body}</p> : null}
      {action ? (
        <Button variant="primary" size="sm" className="mt-1" onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
      {children}
    </div>
  )
}

export interface ErrorStateProps {
  /** What failed, in the user's terms: 'The pipeline could not load'. */
  title: string
  /** The underlying reason, already run through describeError. */
  detail?: string | undefined
  onRetry?: (() => void) | undefined
  className?: string
}

export function ErrorState({ title, detail, onRetry, className }: ErrorStateProps) {
  return (
    <div className={cn('flex flex-col items-start gap-2 rounded-lg border-l-4 border-l-clay bg-[var(--color-clay-wash)] px-4 py-3.5', className)} role="alert">
      <h3 className="text-[length:var(--text-sm)] font-semibold text-clay">{title}</h3>
      {detail ? <p className="measure text-[length:var(--text-sm)] text-ink/80">{detail}</p> : null}
      {onRetry ? (
        <Button size="sm" variant="ghost" className="-ml-2.5 mt-0.5 text-clay" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  )
}

/**
 * The inline note that carries a warning without stopping the page — a settings
 * screen that has never been saved, a paused automation, a quote past its
 * validity date.
 */
export function Notice({
  tone = 'amber',
  title,
  children,
  action,
  className,
}: {
  tone?: 'amber' | 'sage' | 'clay' | 'neutral'
  title: string
  children?: ReactNode
  action?: ReactNode
  className?: string
}) {
  const tones = {
    amber: 'border-l-amber bg-[var(--color-amber-wash)] text-amber',
    sage: 'border-l-sage bg-[var(--color-sage-wash)] text-sage',
    clay: 'border-l-clay bg-[var(--color-clay-wash)] text-clay',
    // Something true but not urgent — an archived record, a paused feature.
    neutral: 'border-l-[var(--color-rule-strong)] bg-[var(--color-wash)] text-taupe',
  } as const

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3 rounded-lg border-l-4 px-4 py-3', tones[tone], className)}>
      <div className="flex flex-col gap-0.5">
        <p className="text-[length:var(--text-sm)] font-medium">{title}</p>
        {children ? <div className="measure text-[length:var(--text-sm)] text-ink/75">{children}</div> : null}
      </div>
      {action}
    </div>
  )
}
